import { memo, useEffect, useCallback } from 'react';
import { useTypewriter } from '../systems/use-typewriter';
import { useAccessibility } from '../systems/accessibility-context';
import { useGame } from '../systems/game-provider';
import { isTypingInEditableField } from '../systems/keyboard-utils';
import {
  DEFAULT_TEXT_OVERLAY_CONTINUE_TEXT,
  type PlayableTextOverlayType,
} from '../systems/text-overlay-content';
import type { CampaignFrise as CampaignFriseData } from '../systems/campaign-frise';
import { CampaignFrise } from './campaign-frise';
import styles from './text-overlay.module.css';

export interface TextOverlayOption {
  text: string;
  condition?: string;
  disabled?: boolean;
}

export interface TextOverlaySection {
  text: string;
  condition?: string;
}

export interface TextOverlayStar {
  condition: string;
}

export interface TextOverlayProps {
  type: PlayableTextOverlayType;
  title?: string;
  /** Letter / cyber overlay — libellé au-dessus du titre. */
  channelLabel?: string;
  characterName?: string;
  characterIcon?: string;
  content: string;
  stars?: TextOverlayStar[];
  reward?: string;
  sections?: TextOverlaySection[];
  options?: TextOverlayOption[];
  onOptionClick?: (index: number) => void;
  onClose: () => void;
  enableTyping?: boolean;
  metadata?: Record<string, string>;
  /** Lettre d'ouverture de mission : bandeau « Mission N sur M » et frise de campagne. */
  missionOpening?: CampaignFriseData | null;
}

/**
 * TextOverlay
 *
 * A generic and reusable component for displaying various types of textual content
 * with distinct visual styles and interactive options.
 */
export const TextOverlay = memo(({
  type,
  title,
  channelLabel,
  characterName,
  characterIcon,
  content,
  stars = [],
  reward,
  sections = [],
  options = [],
  onOptionClick,
  onClose,
  enableTyping: enableTypingProp = false,
  metadata = {},
  missionOpening = null,
}: TextOverlayProps) => {
  const { evaluateCondition } = useGame();
  const { strictMode: a11yStrict } = useAccessibility();
  const enableTyping = enableTypingProp && !a11yStrict;
  const isLetter = type === 'letter';
  const isCyber = type === 'cyber';

  const visibleOptions = options
    .map((option, index) => ({ option, index }))
    .filter(({ option }) => !option.condition || (evaluateCondition && evaluateCondition(option.condition)));

  const { displayedText, isTyping, skipTyping } = useTypewriter(content, {
    speed: 30,
    enabled: enableTyping,
  });

  const finalContent = displayedText;
  const canSkipTyping = enableTyping && isTyping;
  const showOptions = visibleOptions.length > 0 && !canSkipTyping;
  const hasChoiceOptions = visibleOptions.length > 1;
  const continueLabel = visibleOptions[0]?.option.text || DEFAULT_TEXT_OVERLAY_CONTINUE_TEXT;
  const letterBandeau = channelLabel?.trim() || '';

  const continueFromOverlay = useCallback(() => {
    const single = visibleOptions.length === 1 ? visibleOptions[0] : undefined;
    if (single) {
      onOptionClick?.(single.index);
      return;
    }
    if (visibleOptions.length === 0) {
      onClose();
    }
  }, [visibleOptions, onOptionClick, onClose]);

  const handleAdvance = useCallback(() => {
    if (canSkipTyping) {
      skipTyping();
      return;
    }
    if (isLetter && !hasChoiceOptions) {
      continueFromOverlay();
    }
  }, [canSkipTyping, skipTyping, isLetter, hasChoiceOptions, continueFromOverlay]);

  const handleOverlayClick = useCallback(() => {
    if (type === 'summary') return;
    if (canSkipTyping) {
      skipTyping();
      return;
    }
    if (isLetter) {
      if (!hasChoiceOptions) continueFromOverlay();
      return;
    }
    onClose();
  }, [type, canSkipTyping, skipTyping, isLetter, hasChoiceOptions, continueFromOverlay, onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingInEditableField()) return;

      if (e.key === 'Escape' && type !== 'summary') {
        if (isLetter) {
          if (canSkipTyping) {
            skipTyping();
            return;
          }
          if (!hasChoiceOptions) continueFromOverlay();
          return;
        }
        onClose();
        return;
      }

      if (e.key !== ' ') return;
      if (canSkipTyping) {
        e.preventDefault();
        skipTyping();
        return;
      }
      if (isLetter && !hasChoiceOptions) {
        e.preventDefault();
        continueFromOverlay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, type, canSkipTyping, skipTyping, isLetter, hasChoiceOptions, continueFromOverlay]);

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleAdvance();
  };

  const getTypeClassName = () => {
    switch (type) {
      case 'email': return styles.emailContainer;
      case 'console': return styles.consoleContainer;
      case 'terminal': return styles.terminalContainer;
      case 'cyber': return styles.cyberContainer;
      case 'poster': return styles.posterContainer;
      case 'note': return styles.noteContainer;
      case 'document': return styles.documentContainer;
      case 'summary': return styles.summaryContainer;
      default: return '';
    }
  };

  const getContentClassName = () => {
    let className = styles.content;
    if (enableTyping && isTyping) {
      className += ` ${styles.contentTyping}`;
    }

    switch (type) {
      case 'email': className += ` ${styles.emailContent}`; break;
      case 'console': className += ` ${styles.consoleContent}`; break;
      case 'terminal': className += ` ${styles.terminalContent}`; break;
      case 'cyber': className += ` ${styles.cyberContent}`; break;
      case 'poster': className += ` ${styles.posterContent}`; break;
      case 'note': className += ` ${styles.noteContent}`; break;
      case 'document': className += ` ${styles.documentContent}`; break;
      case 'summary': className += ` ${styles.summaryContent}`; break;
    }

    return className;
  };

  if (isLetter) {
    return (
      <div
        className={`${styles.overlay} ${styles.letterOverlay}`}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'overlay-title' : undefined}
      >
        <div className={styles.letterPage} onClick={handleContainerClick}>
          {missionOpening ? (
            <>
              <div className={styles.letterBandeau}>
                Mission {missionOpening.missionPosition}{' '}
                <span className={styles.letterBandeauDe}>sur {missionOpening.missionCount}</span>
              </div>
              <CampaignFrise frise={missionOpening} variant="page" className={styles.letterFrise} />
            </>
          ) : letterBandeau ? (
            <>
              <div className={styles.letterBandeau}>{letterBandeau}</div>
              <div className={styles.letterFilet} />
            </>
          ) : null}

          {(characterName || characterIcon) && (
            <div className={styles.letterSender}>
              {characterIcon && (
                <img src={characterIcon} alt="" className={styles.letterSenderIcon} />
              )}
              {characterName && (
                <span className={styles.letterSenderName}>{characterName}</span>
              )}
            </div>
          )}

          {title ? (
            <h2 id="overlay-title" className={styles.letterTitle}>{title}</h2>
          ) : null}

          {content ? (
            <p className={styles.letterCorps}>
              {finalContent}
              {canSkipTyping && <span className={styles.letterCurseur} aria-hidden="true" />}
            </p>
          ) : null}

          <div className={styles.letterPied}>
            <div className={styles.letterTrait} />
            {showOptions && hasChoiceOptions ? (
              <div className={styles.letterOptions}>
                {visibleOptions.map(({ option, index }) => (
                  <button
                    key={index}
                    type="button"
                    className={styles.letterOptionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOptionClick?.(index);
                    }}
                    disabled={option.disabled}
                  >
                    {option.text}
                  </button>
                ))}
              </div>
            ) : (
              <>
                <div className={styles.letterQuoi}>{continueLabel}</div>
                <span className={styles.letterTouche}>Espace</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.overlay} ${isCyber ? styles.cyberOverlay : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "overlay-title" : undefined}
    >
      <div
        className={`${styles.container} ${getTypeClassName()}`}
        onClick={handleContainerClick}
      >
        {isCyber && (
          <>
            <div className={`${styles.cyberCorner} ${styles.cyberCornerTL}`} />
            <div className={`${styles.cyberCorner} ${styles.cyberCornerTR}`} />
            <div className={`${styles.cyberCorner} ${styles.cyberCornerBL}`} />
            <div className={`${styles.cyberCorner} ${styles.cyberCornerBR}`} />
          </>
        )}

        <div className={`${styles.header} ${isCyber ? styles.cyberTitleBar : ''}`}>
          {isCyber ? (
            <div className={styles.cyberTitleGroup}>
              <span className={styles.cyberChannelLabel}>
                {channelLabel?.trim() || 'Canal sécurisé'}
              </span>
              {title ? (
                <h2 id="overlay-title" className={styles.cyberTitle}>{title}</h2>
              ) : (
                <h2 id="overlay-title" className={styles.cyberTitle}>Message entrant</h2>
              )}
            </div>
          ) : title ? (
            <h2 id="overlay-title" className={styles.title}>{title}</h2>
          ) : (
            <div />
          )}
          <button
            className={`${styles.closeBtn} ${isCyber ? styles.cyberCloseBtn : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            aria-label="Close"
          >
            {isCyber ? '✕' : '×'}
          </button>
        </div>

        {type === 'email' && Object.keys(metadata).length > 0 && (
          <div className={styles.metadata}>
            {Object.entries(metadata).map(([key, value]) => (
              <div key={key} className={styles.metadataRow}>
                <span className={styles.metadataLabel}>{key.charAt(0).toUpperCase() + key.slice(1)}:</span>
                <span className={styles.metadataValue}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {type === 'summary' ? (
          <div className={styles.summaryBody}>
            <div className={styles.summaryColLeft}>
              <div className={styles.summarySections}>
                {finalContent.split('\n\n').filter(Boolean).map((section, i) => {
                  const lines = section.split('\n').filter(Boolean);
                  const headerLine = lines[0];
                  const isSingleLineHeader = lines.length === 1 && headerLine != null && /^[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u.test(headerLine);
                  if (isSingleLineHeader && headerLine) {
                    return <div key={i} className={styles.summarySectionHeader}>{headerLine}</div>;
                  }
                  return (
                    <div key={i} className={styles.summarySection}>
                      {lines.map((line, j) => (
                        <div key={j} className={styles.summaryBullet}>{line}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.summaryColRight}>
              {stars.length > 0 && (() => {
                const sortedStars = [...stars].sort((a, b) => {
                  const earnedA = evaluateCondition ? evaluateCondition(a.condition) : false;
                  const earnedB = evaluateCondition ? evaluateCondition(b.condition) : false;
                  return (earnedA === earnedB) ? 0 : earnedA ? -1 : 1;
                });
                return (
                  <div className={styles.summaryStarsBlock}>
                    <div className={styles.summaryStarsLabel}>Étoiles gagnées</div>
                    <div className={styles.summaryStarsRow}>
                      {sortedStars.map((star, i) => {
                        const earned = evaluateCondition ? evaluateCondition(star.condition) : false;
                        return (
                          <span
                            key={i}
                            className={`${styles.summaryStar} ${earned ? styles.summaryStarEarned : styles.summaryStarEmpty}`}
                            style={{ animationDelay: `${0.6 + i * 0.15}s` }}
                          >
                            ★
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {reward && (
                <div className={styles.summaryReward}>
                  <span className={styles.summaryRewardIcon}>🎁</span>
                  <span>{reward}</span>
                </div>
              )}
              {sections.length > 0 && (() => {
                const visibleSections = sections.filter(
                  s => !s.condition || (evaluateCondition && evaluateCondition(s.condition))
                );
                if (visibleSections.length === 0) return null;
                return (
                  <div className={styles.summaryAchievements}>
                    {visibleSections.map((s, i) => (
                      <div key={i} className={styles.summaryAchievement}>{s.text}</div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : isCyber ? (
          <div
            className={`${styles.cyberBody} ${canSkipTyping ? styles.cyberBodySkippable : ''}`}
            onClick={handleAdvance}
            role={canSkipTyping ? 'button' : undefined}
            tabIndex={canSkipTyping ? 0 : undefined}
            onKeyDown={canSkipTyping ? (e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                handleAdvance();
              }
            } : undefined}
          >
            <div className={styles.cyberDivider} />
            <div className={styles.cyberContentArea}>
              {(characterName || characterIcon) && (
                <div className={styles.cyberSender}>
                  {characterIcon && (
                    <img
                      src={characterIcon}
                      alt=""
                      className={styles.cyberSenderIcon}
                    />
                  )}
                  {characterName && (
                    <span className={styles.cyberSenderName}>{characterName}</span>
                  )}
                </div>
              )}
              <div className={getContentClassName()}>
                {finalContent}
              </div>
              {canSkipTyping && (
                <div className={styles.cyberSkipHint} aria-hidden="true">
                  Clique ou Espace pour continuer
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={canSkipTyping ? styles.contentSkippable : undefined}
            onClick={canSkipTyping ? handleAdvance : undefined}
          >
            <div className={getContentClassName()}>
              {finalContent}
            </div>
            {canSkipTyping && (
              <div className={styles.skipHint} aria-hidden="true">
                Clique ou Espace pour continuer
              </div>
            )}
          </div>
        )}

        {showOptions && (
          <div className={`${styles.optionsContainer} ${isCyber ? styles.cyberOptionsContainer : ''}`}>
            {visibleOptions.map(({ option, index }) => (
              <button
                key={index}
                className={`${styles.optionButton} ${isCyber ? styles.cyberOptionButton : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOptionClick && onOptionClick(index);
                }}
                disabled={option.disabled}
              >
                {isCyber && <span className={styles.cyberOptionArrow}>▶</span>}
                {option.text}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
